import React from 'react';
import { Heading, Text } from '../common/Typography';
import { StatusBadge, Button } from '../common';
import { getClubRoleBadgeClass, getClubRoleLabel } from './ClubPersonalInsights';

/**
 * ClubCardBase - Shared base component for all club card variants
 * Handles layout, container styles, hover states, and accessibility
 */
const ClubCardBase = ({ 
  children, 
  onClick, 
  className = '',
  variant = 'member',
  disabled = false 
}) => {
  const baseClasses = `
    text-left bg-surface border-2 rounded-lg overflow-hidden
    transition-all active:scale-[0.98]
    ${onClick && !disabled ? 'cursor-pointer hover:shadow-md' : ''}
    ${variant === 'member' 
      ? 'border-border hover:border-primary' 
      : 'border-border hover:border-border-medium'
    }
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const handleClick = () => {
    if (onClick && !disabled) {
      onClick();
    }
  };

  const handleKeyDown = (e) => {
    if (onClick && !disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={baseClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      aria-disabled={disabled}
    >
      {children}
    </div>
  );
};

/**
 * ClubCardHeader - Shared header component for club name and location
 */
export const ClubCardHeader = ({ 
  name, 
  location, 
  role, 
  variant = 'member', 
  membershipStatus = null, 
  hasPendingEmailRequest = false,
  hasRecentActivity = false 
}) => {
  const getMembershipBadge = () => {
    if (!membershipStatus) return null;
    
    switch (membershipStatus.status) {
      case 'member':
        return <StatusBadge variant="success" size="small">Member</StatusBadge>;
      case 'pending':
        return <StatusBadge variant="warning" size="small">Request Pending</StatusBadge>;
      case 'link_pending':
        return <StatusBadge variant="info" size="small">Account link requested</StatusBadge>;
      case 'rejected':
        return <StatusBadge variant="danger" size="small">Request Rejected</StatusBadge>;
      case 'inactive':
        return (
          <StatusBadge
            variant={membershipStatus.reactivationRequested ? 'pending' : 'inactive'}
            size="small"
          >
            {membershipStatus.reactivationRequested ? 'Reactivation requested' : 'Inactive'}
          </StatusBadge>
        );
      default:
        return null;
    }
  };
  
  // If no name and no role/badges to show, return null (banner shows name/location)
  const hasContent = name || location || role || membershipStatus || hasPendingEmailRequest || hasRecentActivity;
  if (!hasContent) return null;
  
  return (
    <div className="mb-4">
      {name && (
        <Heading level={4} className="mb-1">
          {name}
        </Heading>
      )}
      {location && (
        <Text className="text-sm text-text-secondary mb-2">
          {location}
        </Text>
      )}
      {(role || membershipStatus || hasPendingEmailRequest || hasRecentActivity) && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {variant === 'member' && role && (
            <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold ${getClubRoleBadgeClass(role)}`}>
              {getClubRoleLabel(role)}
            </span>
          )}
          {variant === 'public' && getMembershipBadge()}
          {variant === 'public' && hasRecentActivity && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-warning-light text-warning-dark">
              🔥 Active this week
            </span>
          )}
          {hasPendingEmailRequest &&
            membershipStatus?.status !== 'link_pending' &&
            membershipStatus?.status !== 'pending' && (
            <StatusBadge variant="info" size="small">
              Account link requested
            </StatusBadge>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * ClubCardStats - Shared stats display component
 */
export const ClubCardStats = ({ stats, variant = 'member', memberCount = null, activity = null }) => {
  if (variant === 'member') {
    if (!stats) return null;

    return (
      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
        <div>
          <div className="text-lg font-bold text-purple-600">
            {stats.mixedElo || 1200}
          </div>
          <div className="text-xs text-text-secondary">Mixed ELO</div>
        </div>
        <div>
          <div className="text-lg font-bold text-blue-600">
            {stats.levelsElo || 1200}
          </div>
          <div className="text-xs text-text-secondary">Levels ELO</div>
        </div>
        <div>
          <div className="text-lg font-bold text-text-primary">
            {stats.totalGamesPlayed || 0}
          </div>
          <div className="text-xs text-text-secondary">Games</div>
        </div>
      </div>
    );
  }

  // Public variant - show member count and activity
  if (variant === 'public') {
    const formatNextSession = (nextSession) => {
      if (!nextSession) return 'No upcoming sessions';
      
      const sessionDate = new Date(nextSession.date);
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // Check if today
      const isToday = sessionDate.toDateString() === now.toDateString();
      // Check if tomorrow
      const isTomorrow = sessionDate.toDateString() === tomorrow.toDateString();
      
      // Format time
      const timeStr = nextSession.time 
        ? new Date(`2000-01-01T${nextSession.time}`).toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          })
        : '';
      
      if (isToday) {
        return `Today${timeStr ? ` at ${timeStr}` : ''}`;
      } else if (isTomorrow) {
        return `Tomorrow${timeStr ? ` at ${timeStr}` : ''}`;
      } else {
        const dayOfWeek = sessionDate.toLocaleDateString('en-US', { weekday: 'short' });
        return `${dayOfWeek}${timeStr ? ` at ${timeStr}` : ''}`;
      }
    };

    return (
      <div className="pt-4 border-t border-border space-y-3">
        {/* Member Count */}
        {memberCount !== null && (
          <div>
            <div className="text-lg font-bold text-text-primary">
              {memberCount}
            </div>
            <div className="text-xs text-text-secondary">
              {memberCount === 1 ? 'Member' : 'Members'}
            </div>
          </div>
        )}
        
        {/* Session Activity */}
        {activity && (
          <div className="space-y-2">
            {/* Upcoming Sessions Count */}
            {activity.upcomingCount > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-info font-medium">
                  📅 {activity.upcomingCount} {activity.upcomingCount === 1 ? 'session' : 'sessions'} this week
                </span>
              </div>
            )}
            
            {/* Next Session */}
            <div className="text-sm text-text-secondary">
              Next: <span className="font-medium text-text-primary">
                {formatNextSession(activity.nextSession)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

/**
 * ClubCardAction - Shared action/CTA component
 * Supports both single label (for members) and multiple buttons (for non-members)
 */
export const ClubCardAction = ({ 
  label, 
  variant = 'member', 
  className = '',
  showButtons = false,
  onViewClub = null,
  onRequestJoin = null,
  onWithdraw = null,
  onRequestReactivate = null,
  onWithdrawReactivate = null,
  isWithdrawing = false,
  membershipStatus = null
}) => {
  const variantStyles = {
    member: 'text-primary',
    public: 'text-text-secondary',
  };

  // Check for cooldown or blocked status
  const isCooldown = membershipStatus?.status === 'cooldown' || membershipStatus?.status === 'blocked';
  const isJoinPending = membershipStatus?.status === 'pending';
  const isLinkPending = membershipStatus?.status === 'link_pending';
  const isInactive = membershipStatus?.status === 'inactive';
  const reactivationRequested = Boolean(membershipStatus?.reactivationRequested);
  const cooldownInfo = membershipStatus?.cooldownInfo;
  
  // Format cooldown message
  const getCooldownMessage = () => {
    if (!cooldownInfo) return null;
    
    if (cooldownInfo.is_blocked) {
      return 'You are blocked from requesting to join this club.';
    }
    
    if (cooldownInfo.cooldown_until) {
      const cooldownDate = new Date(cooldownInfo.cooldown_until);
      const now = new Date();
      
      if (cooldownDate > now) {
        const daysUntil = Math.ceil((cooldownDate - now) / (1000 * 60 * 60 * 24));
        const formattedDate = cooldownDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
        
        if (daysUntil === 1) {
          return `You can request to join again tomorrow (${formattedDate}).`;
        } else if (daysUntil <= 7) {
          return `You can request to join again in ${daysUntil} days (${formattedDate}).`;
        } else {
          return `You can request to join again on ${formattedDate}.`;
        }
      }
    }
    
    return null;
  };
  
  const cooldownMessage = getCooldownMessage();

  if (showButtons && isInactive) {
    return (
      <div className={`mt-4 flex flex-col gap-2 ${className}`}>
        <p className="text-xs text-text-secondary">
          {reactivationRequested
            ? 'Your reactivation request is waiting on a club admin.'
            : 'Your membership is inactive. Request to reactivate to join sessions again.'}
        </p>
        <div className="flex gap-2">
          {onRequestReactivate && !reactivationRequested ? (
            <Button
              variant="primary"
              size="small"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                onRequestReactivate();
              }}
              disabled={isWithdrawing}
            >
              {isWithdrawing ? 'Sending…' : 'Request to reactivate'}
            </Button>
          ) : null}
          {onViewClub ? (
            <Button
              variant={reactivationRequested || !onRequestReactivate ? 'primary' : 'outline'}
              size="small"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                onViewClub();
              }}
            >
              View club
            </Button>
          ) : null}
        </div>
        {reactivationRequested && onWithdrawReactivate ? (
          <Button
            variant="outline"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onWithdrawReactivate();
            }}
            disabled={isWithdrawing}
          >
            {isWithdrawing ? 'Updating…' : 'Withdraw request'}
          </Button>
        ) : null}
      </div>
    );
  }

  if (showButtons && (onViewClub || onRequestJoin || onWithdraw || isCooldown || isJoinPending || isLinkPending)) {
    return (
      <div className={`mt-4 flex flex-col gap-2 ${className}`}>
        <div className="flex gap-2">
          {onViewClub && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewClub();
              }}
              className="flex-1 px-4 py-2 text-sm font-medium bg-primary text-text-inverse rounded-lg hover:bg-primary-dark transition-colors"
            >
              View Club
            </button>
          )}
          {onRequestJoin && !isCooldown && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRequestJoin();
              }}
              className="flex-1 px-4 py-2 text-sm font-medium bg-primary text-text-inverse rounded-lg hover:bg-primary-dark transition-colors"
            >
              Request Join
            </button>
          )}
          {(isJoinPending || isLinkPending) && !onRequestJoin && (
            <button
              type="button"
              disabled
              className="flex-1 px-4 py-2 text-sm font-medium bg-border-medium text-text-muted rounded-lg cursor-not-allowed opacity-60"
            >
              {isLinkPending ? 'Link requested' : 'Request pending'}
            </button>
          )}
          {isCooldown && !onRequestJoin && (
            <button
              disabled
              className="flex-1 px-4 py-2 text-sm font-medium bg-border-medium text-text-muted rounded-lg cursor-not-allowed opacity-60"
            >
              Request Join
            </button>
          )}
        </div>
        {cooldownMessage && (
          <p className="text-xs text-text-secondary text-center px-2 py-1 bg-surface-subtle rounded">
            {cooldownMessage}
          </p>
        )}
        {onWithdraw && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onWithdraw();
            }}
            disabled={isWithdrawing}
            className="w-full px-4 py-2 text-xs font-medium bg-primary text-text-inverse rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isWithdrawing ? 'Withdrawing...' : 'Withdraw Request'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`mt-4 text-sm font-medium ${variantStyles[variant]} ${className}`}>
      {label}
    </div>
  );
};

export default ClubCardBase;

